import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Chip,
  IconButton,
  Menu,
  useMediaQuery,
  useTheme,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  
} from "@mui/material";
import { MoreVert, Search } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import AddBranch from "./AddBranch";
import {
  getAllBranches,
  deleteBranch,
  createBranch,
  updateBranch,
} from "../../service/branchService";
import { useSnackbar } from "../../App";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Color from "../../components/Color";

const Branches = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down("xs"));
  const { showSnackbar } = useSnackbar();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [searchBranch, setSearchBranch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchStatus, setSearchStatus] = useState("All");
  const [openModal, setOpenModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const open = Boolean(anchorEl);

  // Confirm dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    address: "",
    phone: "",
    email: "",
    is_active: true,
  });

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const fetchBranches = useCallback(async () => {
    setLoading(true);
    try {
      const page = paginationModel.page + 1;
      const limit = paginationModel.pageSize;
      const sortField = sortModel[0]?.field || "id";
      const sortOrder = sortModel[0]?.sort || "desc";

      const res = await getAllBranches(
        page,
        limit,
        debouncedSearch || "",
        sortField,
        sortOrder
      );

      const data = res?.data?.data;
      if (Array.isArray(data?.data)) {
        setRows(data.data);
        setRowCount(data.total);
      } else {
        setRows([]);
        showSnackbar("Unexpected data format!", "error");
      }
    } catch (error) {
      showSnackbar("Failed to fetch branches!", "error");
    }
    setLoading(false);
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    debouncedSearch,
    sortModel,
    showSnackbar,
  ]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      setDebouncedSearch(searchBranch.trim());
    }, 500);
    return () => clearTimeout(handler);
  }, [searchBranch]);

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteBranch(deleteId);
      showSnackbar("Branch deleted successfully!", "success");
      fetchBranches();
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      if (errorMessage === "Cannot delete branch with associated users") {
        showSnackbar(errorMessage, "warning");
      } else {
        showSnackbar("Failed to delete branch!", "error");
      }
    } finally {
      setConfirmOpen(false);
    }
  };


  const handleSubmit = async (errorsCallback) => {
    try {
      const payload = { ...formData, status: formData.is_active };
      if (editingId) {
        await updateBranch(editingId, payload);
        showSnackbar("Branch updated successfully!", "success");
      } else {
        await createBranch(payload);
        showSnackbar("Branch added successfully!", "success");
      }

      setOpenModal(false);
      setEditingId(null);
      setFormData({
        name: "",
        code: "",
        address: "",
        phone: "",
        email: "",
        is_active: true,
      });
      fetchBranches();
    } catch (error) {
      if (error.response?.data?.errors) {
        const apiErrors = error.response.data.errors;
        errorsCallback(apiErrors); // Pass errors to AddBranch
        // Specific message for code uniqueness error
        const errorMessage =
          apiErrors.code?.[0] === "The code has already been taken."
            ? "The branch code is already taken. Please use a different code."
            : "Validation failed. Please check the form.";
        showSnackbar(errorMessage, "error");
      } else {
        throw error
        showSnackbar("Something went wrong!", "error");
      }
    }
  };

  const handleEdit = (branch) => {
    setFormData({
      name: branch.name || "",
      code: branch.code || "",
      address: branch.address || "",
      phone: branch.phone || "",
      email: branch.email || "",
      is_active: branch.is_active === true || branch.is_active === 1,
    });
    setEditingId(branch.id);
    setOpenModal(true);
    handleMenuClose();
  };

  const handleClear = () => {
    setSearchBranch("");
    setSearchStatus("All");
  };

  const matchesStatus = (row) => {
    if (searchStatus === "All") return true;
    if (searchStatus === "Active") return row.is_active === true;
    if (searchStatus === "Inactive") return row.is_active === false;
    return true;
  };

  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            onClick={(e) => handleMenuOpen(e, params.row)}
            sx={{
              backgroundColor: "rgba(0,0,0,0.04)",
              borderRadius: 1.5,
              padding: isMobile ? "10px" : "8px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.08)",
              },
            }}
          >
            <MoreVert sx={{ fontSize: isMobile ? 18 : 16 }} />
          </IconButton>
        </Box>
      ),
    },
    { field: "code", headerName: "Branch Code", flex: 0.8, minWidth: isMobile ? 100 : 150 },
    { field: "name", headerName: "Branch Name", flex: 1.2, minWidth: isMobile ? 120 : 150 },
    { field: "phone", headerName: "Phone", flex: 1, minWidth: isMobile ? 100 : 120 },
    { field: "email", headerName: "Email", flex: 1, minWidth: 150 },
    { field: "address", headerName: "Address", flex: 2, minWidth: 200, maxWidth: 350 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: isMobile ? 80 : 100,
      renderCell: (params) => (
        <Chip
          label={params.row.is_active ? "Active" : "Inactive"}
          color={params.row.is_active ? "success" : "error"}
          size={isMobile ? "small" : "medium"}
        />
      ),
    },
  ];

  return (
    <Box sx={{ padding: isMobile ? 1 : 0 }}>
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent={isMobile ? "stretch" : { sm: "flex-start", md: "flex-start", lg: "space-between" }}
        alignItems={isMobile ? "stretch" : "center"}
        flexWrap="wrap"
        gap={isMobile ? 1 : 2}
        mt={2}
      >
        <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 1 : "10px", width: isMobile ? "100%" : "auto" }}>
          <TextField
            value={searchBranch}
            onChange={(e) => setSearchBranch(e.target.value.trimStart())}
            placeholder="Search branch name"
            variant="outlined"
            InputLabelProps={{ shrink: false }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: isMobile ? 18 : 20 }} />
                </InputAdornment>
              ),
              endAdornment: searchBranch && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchBranch("");
                      setDebouncedSearch("");
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                  >
                    ✖
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputLabel-root": { display: "none" },
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: "#7C0BCC",
                },
                height: isMobile ? "34px" : "37.69px",
                fontSize: isMobile ? "0.85rem" : "1rem",
              },
              width: isMobile ? "100%" : { sm: 180, md: 220, lg: 250 },
              mb: isMobile ? 0 : 0,
            }}
          />
          <FormControl sx={{ width: isMobile ? "100%" : { sm: 120, md: 140, lg: 150 }, mb: isMobile ? 1 : 0 }}>
            <Select
              value={searchStatus}
              onChange={(e) => setSearchStatus(e.target.value)}
              displayEmpty
              renderValue={(value) => {
                if (!value) return <Box sx={{ color: "text.secondary" }}>Select status</Box>;
                return value;
              }}
              sx={{
                "& .MuiInputLabel-root": { display: "none" },
                "& .MuiSelect-select": {
                  fontSize: isMobile ? "0.85rem" : "0.9rem",
                },
                height: isMobile ? "34px" : "37.69px",
                width: "100%",
              }}
            >
              <MenuItem value="" disabled>
                Select status
              </MenuItem>
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
          <Button
            onClick={handleClear}
            sx={{
              height: isMobile ? "34px" : "36px",
              background: "none",
              width: isMobile ? "100%" : { sm: 80, md: 90, lg: 90 },
              color: Color.secondary.main,
              border: "1px solid",
              borderColor: Color.secondary.main,
              fontSize: isMobile ? "0.85rem" : "1rem",
              padding: isMobile ? "8px" : "6px 16px",
            }}
          >
            Clear
          </Button>
        </Box>

        <Button
          sx={{
            background: Color.primary.main,
            color: "white",
            height: isMobile ? "34px" : "37.69px",
            width: isMobile ? "100%" : { sm: 180, md: 190, lg: 200 },
            whiteSpace: "nowrap",
            fontSize: isMobile ? "0.85rem" : "1rem",
            mt: isMobile ? 0 : 0,
            "&:hover": {
              background: Color.primary.dark,
            },
          }}
          onClick={() => {
            setEditingId(null);
            setFormData({
              name: "",
              code: "",
              address: "",
              phone: "",
              email: "",
              is_active: true,
            });
            setOpenModal(true);
          }}
          startIcon={<AddIcon />}
        >
          Add Branch
        </Button>
      </Box>

      <Box sx={{ width: "100%", mt: isMobile ? 2 : 2 }}>
        <DataGrid
          rows={rows.filter(
            (row) =>
              row.name?.toLowerCase().includes(searchBranch.trim().toLowerCase()) &&
              matchesStatus(row)
          )}
          columns={columns.map((col) => ({
            ...col,
            flex: col.flex || 1,
            minWidth: col.minWidth || (isMobile ? 80 : 100),
          }))}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={rowCount}
          rowHeight={isMobile ? 48 : 60}
          paginationMode="server"
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          loading={loading}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          autoHeight
          disableColumnMenu
          disableColumnSorting
          sx={{
            fontFamily: '"Poppins", sans-serif',
            borderRadius: "10px",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: isMobile ? "14px" : "16px",
              fontWeight: 600,
              pl: 0,
            },
            "& .MuiDataGrid-cell": {
              fontSize: isMobile ? "12px" : "14px",
              pl: 1,
            },
            "& .MuiDataGrid-row:nth-of-type(even)": {
              backgroundColor: "#fff",
            },
            "& .MuiDataGrid-row:nth-of-type(odd)": {
              backgroundColor: "#f4f4f4",
            },
            "& .MuiLinearProgress-bar": {
              backgroundColor: Color.primary.main,
            },
            "& .MuiLinearProgress-colorPrimary": {
              backgroundColor: Color.secondary.main,
            },
            overflowX: isMobile ? "hidden" : "auto",
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
              border: "2px solid",
              borderColor: Color.primary.main,
              borderRadius: "4px",
            },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
              outline: "none",
              border: "2px solid",
              borderColor: Color.primary.main,
              borderRadius: "4px",
            },
          }}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            maxHeight: 200,
            width: isMobile ? "90vw" : 200,
          },
        }}
      >
        <MenuItem
          onClick={() => handleEdit(selectedRow)}
          sx={{ fontSize: isMobile ? "0.85rem" : "1rem", padding: isMobile ? "10px" : "8px" }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => handleDeleteClick(selectedRow?.id)}
          sx={{ fontSize: isMobile ? "0.85rem" : "1rem", padding: isMobile ? "10px" : "8px" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1, color: "error.main" }} /> Delete
        </MenuItem>
      </Menu>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            width: isMobile ? "90vw" : "auto",
            m: isMobile ? 1 : 2,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: isMobile ? "1rem" : "1.25rem" }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: isMobile ? "0.85rem" : "1rem" }}>
            Are you sure you want to delete this branch?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ mr: 2, mb: 2, flexDirection: isMobile ? "column" : "row", gap: isMobile ? 1 : 0 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            sx={{
              border: "1px solid",
              borderColor: Color.secondary.main,
              color: Color.label.main,
              width: isMobile ? "100%" : "auto",
              fontSize: isMobile ? "0.85rem" : "1rem",
              padding: isMobile ? "8px" : "6px 16px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            sx={{
              background: Color.primary.main,
              color: "white",
              width: isMobile ? "100%" : "auto",
              fontSize: isMobile ? "0.85rem" : "1rem",
              padding: isMobile ? "8px" : "6px 16px",
              "&:hover": {
                background: Color.primary.dark,
              },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <AddBranch
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setEditingId(null);
          setFormData({
            name: "",
            code: "",
            address: "",
            phone: "",
            email: "",
            is_active: true,
          });
        }}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        editingId={editingId}
      />
    </Box>
  );
};

export default Branches;